// ============================================================================
// JARVISTRAVEL - BACKEND REACHABILITY STORE (JAR-314)
// Whether the app can currently reach its backend. Kept as a module-level
// store (same pattern as Toast) so the plain-async API client can report
// reachability from OUTSIDE React — a React context can't be read from
// client.ts. Components subscribe via useSyncExternalStore (see
// BackendUnavailableBanner). Binary by design: 'offline' covers both a dead
// network and a 5xx outage; both mean "we couldn't serve you," which is the
// one thing the banner communicates.
// ============================================================================

export type BackendStatus = 'online' | 'offline';

// Optimistic default: assume reachable until a real request proves otherwise,
// so the banner never flashes on a healthy cold start.
let status: BackendStatus = 'online';
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export function getBackendStatus(): BackendStatus {
  return status;
}

export function subscribeBackendStatus(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** A request failed to reach/serve (network error, timeout, or 5xx). */
export function reportBackendUnreachable(): void {
  if (status !== 'offline') { status = 'offline'; emit(); }
}

/** The backend answered (any response < 500) — clear a stale offline state. */
export function reportBackendReachable(): void {
  if (status !== 'online') { status = 'online'; emit(); }
}

/** Test-only: reset module state between cases. */
export function __resetBackendStatus(): void {
  status = 'online';
  listeners.clear();
}
